import { useCallback, useEffect, useRef, useState } from 'react';

export type ImageEyedropperStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseImageEyedropperResult {
  /** Loading lifecycle of the background image used for sampling. */
  status: ImageEyedropperStatus;
  /** True when a pixel can be sampled (image loaded onto an untainted canvas). */
  enabled: boolean;
  /**
   * Sample the colour at normalised image coordinates (`rx`, `ry` ∈ [0,1]).
   * Returns canonical `#RRGGBB`, or null when unavailable (no image, not loaded,
   * or the canvas is tainted — in which case the hook switches to `error`).
   */
  pickColorAtRatio: (rx: number, ry: number) => string | null;
}

function toHexByte(n: number): string {
  return Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
}

/**
 * Canvas image-pipette hook (Spec 340): loads the service's background image with
 * `crossorigin="anonymous"` onto an offscreen canvas so the editor can sample a
 * colour from it. Disables cleanly when there is no image, the image fails to load,
 * or the canvas is tainted (CORS) — `getImageData` then throws and we surface `error`
 * without affecting the rest of the editor.
 */
export function useImageEyedropper(imageUrl?: string | null): UseImageEyedropperResult {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dimsRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const [status, setStatus] = useState<ImageEyedropperStatus>('idle');

  useEffect(() => {
    canvasRef.current = null;
    dimsRef.current = { w: 0, h: 0 };

    if (!imageUrl) {
      setStatus('idle');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx || canvas.width === 0 || canvas.height === 0) {
          setStatus('error');
          return;
        }
        ctx.drawImage(img, 0, 0);
        // Probe for taint up front so we disable before the user clicks.
        ctx.getImageData(0, 0, 1, 1);
        canvasRef.current = canvas;
        dimsRef.current = { w: canvas.width, h: canvas.height };
        setStatus('ready');
      } catch {
        setStatus('error');
      }
    };
    img.onerror = () => {
      if (!cancelled) setStatus('error');
    };
    img.src = imageUrl;

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const pickColorAtRatio = useCallback((rx: number, ry: number): string | null => {
    const canvas = canvasRef.current;
    const { w, h } = dimsRef.current;
    if (!canvas || w === 0 || h === 0) return null;

    const px = Math.min(w - 1, Math.max(0, Math.round(rx * w)));
    const py = Math.min(h - 1, Math.max(0, Math.round(ry * h)));
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const [r, g, b] = ctx.getImageData(px, py, 1, 1).data;
      return `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}`.toUpperCase();
    } catch {
      setStatus('error');
      return null;
    }
  }, []);

  return { status, enabled: status === 'ready', pickColorAtRatio };
}
